/**
 * Camera utility functions for the chat application
 */

/**
 * Check if camera access is supported in the current browser
 * @returns {boolean} True if camera access is supported
 */
export const isCameraSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * Get available camera devices
 * @returns {Promise<MediaDeviceInfo[]>} Array of camera devices
 */
export const getCameraDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'videoinput');
  } catch (error) {
    console.error('Error getting camera devices:', error);
    return [];
  }
};

/**
 * Check if device has multiple cameras (front and back)
 * @returns {Promise<boolean>} True if device has multiple cameras
 */
export const hasMultipleCameras = async () => {
  const cameras = await getCameraDevices();
  return cameras.length > 1;
};

/**
 * Convert a data URL to a File object
 * @param {string} dataUrl - The data URL to convert
 * @param {string} filename - The filename for the file
 * @returns {File} The File object
 */
export const dataUrlToFile = (dataUrl, filename) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mime });
};

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width for the compressed image
 * @param {number} maxHeight - Maximum height for the compressed image
 * @param {number} quality - Compression quality (0-1)
 * @returns {Promise<File>} The compressed image file
 */
export const compressImage = (file, maxWidth = 1280, maxHeight = 720, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        const compressedFile = new File([blob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        resolve(compressedFile);
      }, 'image/jpeg', quality);
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get camera constraints based on facing mode and quality preference
 * @param {string} facingMode - 'user' or 'environment'
 * @param {string} quality - 'low', 'medium', or 'high'
 * @returns {object} Media constraints object
 */
export const getCameraConstraints = (facingMode = 'user', quality = 'medium') => {
  const qualitySettings = {
    low: { width: 640, height: 480 },
    medium: { width: 1280, height: 720 },
    high: { width: 1920, height: 1080 }
  };

  const settings = qualitySettings[quality] || qualitySettings.medium;

  return {
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: settings.width },
      height: { ideal: settings.height }
    },
    audio: false
  };
};
